"use client";

import { useMemo } from "react";
import { useAuthContext } from "@/contexts/common/AuthContextProvider";
import { GailProductRoles } from "@/enums/user";
import { useOrg } from "@/hooks/useOrg";
import GailAgentNavContainer from "./navContainers/GailAgentNavContainer";
import GailGptNavContainer from "./navContainers/GailGptNavContainer";
import NoProductAccessNavContainer from "./navContainers/NoProductAccessNavContainer";

enum NavContainerType {
  GAILGPT = "gailgpt",
  GAILAGENT = "gailagent",
  NO_ACCESS = "no_access",
}

interface NavContainerProps {
  readonly children: React.ReactNode;
  readonly setShowUserManagement: (value: boolean) => void;
  readonly hasError?: boolean;
}

function deriveNavContainerType(
  productRoles: GailProductRoles[] | undefined,
  isOrgGailGptV1: boolean
): NavContainerType {
  if (isOrgGailGptV1) return NavContainerType.GAILAGENT;

  if (productRoles?.includes(GailProductRoles.GailAgentUser)) {
    return NavContainerType.GAILAGENT;
  }

  if (productRoles?.includes(GailProductRoles.GailGPTUser)) {
    return NavContainerType.GAILGPT;
  }

  return NavContainerType.NO_ACCESS;
}

export default function NavContainer({
  children,
  setShowUserManagement,
  hasError,
}: NavContainerProps) {
  const { productRolesState } = useAuthContext();
  const { productAccess } = useOrg();

  const navContainerType = useMemo(
    () =>
      productRolesState.loaded
        ? deriveNavContainerType(
            productRolesState.roles,
            productAccess.isOrgGailGptV1
          )
        : null,
    [productRolesState.loaded, productRolesState.roles, productAccess.isOrgGailGptV1]
  );

  if (hasError || navContainerType === null) {
    return children;
  }

  const onUserManagementClick = () => setShowUserManagement(true);

  switch (navContainerType) {
    case NavContainerType.GAILGPT:
      return (
        <GailGptNavContainer onUserManagementClick={onUserManagementClick}>
          {children}
        </GailGptNavContainer>
      );
    case NavContainerType.GAILAGENT:
      return (
        <GailAgentNavContainer onUserManagementClick={onUserManagementClick}>
          {children}
        </GailAgentNavContainer>
      );
    case NavContainerType.NO_ACCESS:
    default:
      return (
        <NoProductAccessNavContainer
          onUserManagementClick={onUserManagementClick}
        >
          {children}
        </NoProductAccessNavContainer>
      );
  }
}
